import type { TrainingPlan } from '@/types/curriculum';
import type { ProgramDrill, TrainingProgram } from '@/types/training-program';
import { loadTrainingDrill } from '@/lib/training-program/drill-loader';
import { loadOptionalDrillSelection } from '@/lib/training-program/optional-drills';
import { buildSessionMap, buildTrainingProgram, getCurrentWeekIndex, mapTrainingDrillToProgram } from '@/lib/training-program/program-mapper';

export type LoadedTrainingProgram = {
  program: TrainingProgram;
  requiredDrills: ProgramDrill[];
  optionalDrills: ProgramDrill[];
  currentWeekIndex: number;
};

const loadProgramDrills = async (
  drillIds: string[],
  sessionMap: Record<string, { session: TrainingPlan['weeks'][number]['sessions'][number] }>,
  isRequired: boolean
) => {
  const uniqueIds = Array.from(new Set(drillIds.filter(Boolean)));
  const drills = await Promise.all(uniqueIds.map((id) => loadTrainingDrill(id)));
  return drills
    .filter(Boolean)
    .map((drill) => {
      const session = sessionMap[drill!.drill_id]?.session;
      return mapTrainingDrillToProgram(drill!, { isRequired, session });
    });
};

export const loadTrainingProgram = async (plan: TrainingPlan): Promise<LoadedTrainingProgram | null> => {
  if (!plan || !plan.weeks || plan.weeks.length === 0) return null;

  const currentWeekIndex = getCurrentWeekIndex(plan);
  const currentWeek = plan.weeks[currentWeekIndex] ?? plan.weeks[0];
  const sessionMap = buildSessionMap(plan);
  const requiredDrillIds = currentWeek.sessions.map((session) => session.drill_id);

  const requiredDrills = await loadProgramDrills(requiredDrillIds, sessionMap, true);

  const optionalSelection = loadOptionalDrillSelection(plan.id).drillIds;
  const optionalDrills = await loadProgramDrills(optionalSelection, sessionMap, false);
  const requiredIds = new Set(requiredDrills.map((drill) => drill.drill_id));
  const filteredOptional = optionalDrills.filter((drill) => !requiredIds.has(drill.drill_id));

  const program = buildTrainingProgram({
    plan,
    requiredDrills,
    optionalDrills: filteredOptional,
    currentWeekIndex,
  });

  return {
    program,
    requiredDrills,
    optionalDrills: filteredOptional,
    currentWeekIndex,
  };
};
